import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/content";

const LINES = [
  "Commercial",
  "Landscape",
  "Construction",
  "& Maintenance",
];

const lineWrap = { hidden: {}, show: {} };
const lineInner = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroExcavator}
          alt="Yellow excavator performing commercial landscape construction"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-obsidian/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/20" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 w-full mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12 pb-16 sm:pb-20 pt-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-brand" />
          <span className="text-xs uppercase tracking-[0.3em] text-brand font-semibold">
            Licensed · Bonded · Insured — AR & MS
          </span>
        </motion.div>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="font-display font-extrabold uppercase tracking-tighter text-[#f2f4f0] leading-[0.92] text-5xl sm:text-7xl lg:text-8xl xl:text-9xl"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                variants={lineInner}
                custom={i}
                className={`block ${i === 3 ? "text-brand" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#c7ccc4] leading-relaxed"
          data-testid="hero-subheadline"
        >
          Serving schools, colleges, new buildings, and commercial
          infrastructure across Arkansas and Mississippi.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-brand px-7 py-4 font-bold text-[#0b120a] hover:bg-brand-hover transition-colors duration-200 text-sm uppercase tracking-wide"
          >
            Request a Quote
          </a>
          <a
            href={BUSINESS.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/25 px-7 py-4 font-bold text-[#f2f4f0] hover:border-brand hover:text-brand transition-colors duration-200 text-sm uppercase tracking-wide"
          >
            <Phone className="h-4 w-4" />
            Call {BUSINESS.phone}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-[#8d948f]"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
        <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
      </motion.div>
    </section>
  );
};
